import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const EditProfile = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("");
  const [fundingRequired, setFundingRequired] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      if (!auth.currentUser) return;

      try {
        const docRef = doc(db, "users", auth.currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setFullName(data.fullName || "");
          setEmail(data.email || auth.currentUser.email);
          setBio(data.bio || "");
          setBusinessName(data.businessName || "");
          setIndustry(data.industry || "");
          setLocation(data.location || "");
          setFundingRequired(data.fundingRequired || "");
        } else {
          console.log("No profile data found — new user.");
        }
      } catch (error) {
        console.error("Error loading profile data:", error);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchUserData();
  }, []);

  if (loadingProfile) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex items-center space-x-3">
          <svg
            className="animate-spin h-6 w-6 text-indigo-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          <span className="text-xl font-medium text-gray-700">
            Loading profile...
          </span>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      alert("You must be logged in to submit a profile.");
      return;
    }

    setLoading(true);

    try {
      const profileData = {
        fullName,
        email,
        bio,
        businessName,
        industry: industry.trim(),
        location,
        fundingRequired,
        updatedAt: new Date(),
      };

      await setDoc(doc(db, "users", auth.currentUser.uid), profileData);
      alert("Profile saved successfully! 🎉 Redirecting to dashboard...");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("Error saving profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Edit Your Profile</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Full Name */}
        <div>
          <label className="block font-semibold mb-1">Full Name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full border px-3 py-2 rounded"
            placeholder="Your full name"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block font-semibold mb-1">Email</label>
          <input
            type="email"
            required
            value={email}
            readOnly
            className="w-full border px-3 py-2 rounded bg-gray-100"
          />
        </div>

        {/* Business Name */}
        <div>
          <label className="block font-semibold mb-1">Business Name</label>
          <input
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            className="w-full border px-3 py-2 rounded"
            placeholder="Your business or startup name"
          />
        </div>

        {/* Industry */}
        <div>
          <label className="block font-semibold mb-1">Industry</label>
          <input
            type="text"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full border px-3 py-2 rounded"
            placeholder="Industry (e.g., Real Estate, Media, Manufacturing)"
            required
          />
        </div>

        {/* Location */}
        <div>
          <label className="block font-semibold mb-1">Location</label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="w-full border px-3 py-2 rounded"
            placeholder="City, Country"
          />
        </div>

        {/* Funding Required */}
        <div>
          <label className="block font-semibold mb-1">Funding Required</label>
          <input
            type="text"
            value={fundingRequired}
            onChange={(e) => setFundingRequired(e.target.value)}
            className="w-full border px-3 py-2 rounded"
            placeholder="e.g., $50,000 - $100,000"
          />
        </div>

        {/* Bio / Pitch */}
        <div>
          <label className="block font-semibold mb-1">Bio / Pitch</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={4}
            className="w-full border px-3 py-2 rounded"
            placeholder="Brief description or elevator pitch"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className={`w-full py-3 rounded font-semibold text-white ${
            loading ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"
          } transition`}
        >
          {loading ? "Saving..." : "Submit Profile"}
        </button>
      </form>
    </div>
  );
};

export default EditProfile;

